/**
 * useMessengerCalls — WebRTC voice/video call manager.
 *
 * Hooks into the existing /api/ws/messages WebSocket to send + receive
 * signalling frames (call_invite, call_accept, call_decline,
 * call_cancel, call_end, webrtc_offer, webrtc_answer,
 * ice_candidate) and maintains a single RTCPeerConnection per active
 * call. Audio/video tracks attach automatically to local + remote
 * <video> refs that the UI passes in.
 *
 * Public API
 * ----------
 *   const {
 *     call,            // null | { id, conversationId, peerId, type, direction, status, peerName, peerAvatar, startedAt }
 *     muted, cameraOff,
 *     localStream, remoteStream,
 *     placeCall(conversationId, peerId, type, { peerName, peerAvatar }),
 *     acceptCall(type), // type can override the invite type (e.g. accept video as voice)
 *     declineCall(),
 *     endCall(),
 *     toggleMute(), toggleCamera(),
 *   } = useMessengerCalls({ wsRef, currentUserId, axios, API, AX });
 *
 * The hook does NOT render anything — CallOverlay.jsx owns the UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { startCallTone } from '../utils/callTones';
import { setActiveMediaSession } from '../api/apiClient';

const RING_TIMEOUT_MS = 35_000; // give the callee 35 s to answer

export default function useMessengerCalls({ getWs, currentUserId, axios, API, AX }) {
  const [call, setCall] = useState(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  // Live refs so signalling handlers (registered once) see the latest
  // call state without re-binding.
  const callRef = useRef(null);
  const outcomeToneRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const iceServersRef = useRef([{ urls: 'stun:stun.l.google.com:19302' }]);
  const ringTimerRef = useRef(null);
  // Buffer ICE candidates that arrive before the remote description
  // is set (race with the offer/answer round-trip).
  const pendingIceRef = useRef([]);
  // Iter156 — buffer for an SDP offer that arrives BEFORE the callee
  // taps Accept (the offer almost always arrives within ms of the
  // call_invite, but the user takes seconds to react). Without this
  // buffer, the offer was dropped on the floor and the callee never
  // sent an answer — caller's PC then never got setRemoteDescription
  // applied, so both sides saw their own video but no remote.
  const pendingOfferRef = useRef(null);
  // Iter158 — single SYNCHRONOUS media lock. Set BEFORE any async
  // work in placeCall/acceptCall, cleared in the central cleanup
  // helper. Solves the "Your microphone or camera is being used by
  // another app" red herring: it was caused by double-clicks (and
  // StrictMode dev double-invocation) racing two getUserMedia
  // requests — the second one hits NotReadableError because the
  // first already owns the device. Surfaced via `mediaInProgress`
  // state so the call buttons can be disabled while a request is
  // in flight.
  const mediaLockRef = useRef(false);
  const [mediaInProgress, setMediaInProgress] = useState(false);

  const acquireMediaLock = useCallback(() => {
    outcomeToneRef.current?.();
    outcomeToneRef.current = null;
    if (mediaLockRef.current) {

      console.info('[ccuk-call]', 'media-lock-skipped-duplicate');
      return false;
    }
    mediaLockRef.current = true;
    setMediaInProgress(true);
    // Iter-rescue 2026-02: hold the global active-media-session pause
    // for the duration of media acquisition + active call so
    // non-essential background polling (notifications, presence) stands
    // down and we don't contend with the camera/mic + signalling paths.
    setActiveMediaSession(true);

    console.info('[ccuk-call]', 'media-lock-acquired');
    return true;
  }, []);

  const releaseMediaLock = useCallback(() => {
    if (!mediaLockRef.current) return;
    mediaLockRef.current = false;
    setMediaInProgress(false);
    setActiveMediaSession(false);
  }, []);

  // Central, idempotent cleanup. Single source of truth for every
  // failure/end/cancel/decline/missed path. Always safe to call.
  const cleanupCallResources = useCallback((reason) => {

    console.info('[ccuk-call]', 'cleanup-start', { reason });
    // Local media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      localStreamRef.current = null;
    }
    setLocalStream(null);
    // Peer connection
    if (pcRef.current) {
      try { pcRef.current.getSenders().forEach((s) => { try { s.track && s.track.stop(); } catch { /* ignore */ } }); } catch { /* ignore */ }
      try { pcRef.current.close(); } catch { /* ignore */ }
      pcRef.current = null;
    }
    // Remote stream
    remoteStreamRef.current = null;
    setRemoteStream(null);
    // Pending buffers + counters
    pendingIceRef.current = [];
    pendingOfferRef.current = null;
    iceSentRef.current = 0;
    iceRcvdRef.current = 0;
    pendingOutboundRef.current = [];
    // Timers
    if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
    // Media lock
    releaseMediaLock();

    console.info('[ccuk-call]', 'cleanup-complete', { reason });
  }, [releaseMediaLock]);
  // Iter156 — diagnostic counters (no PII). Surfaced via safeLog so
  // we can prove from a user's console that ICE candidates are
  // actually flowing both ways.
  const iceSentRef = useRef(0);
  const iceRcvdRef = useRef(0);
  // Debounce the "WS not ready" toast so a user rapidly tapping
  // Voice/Video while reconnecting doesn't see a stack of identical
  // messages. One toast every 5 s is plenty.
  const lastNotReadyToastRef = useRef(0);

  // Mirror state into refs for signalling handler stability.
  useEffect(() => { callRef.current = call; }, [call]);

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------

  // Iter157 — outbound signalling queue. The WS can be briefly
  // CLOSED/CONNECTING during a reconnect (network blip, focus
  // recovery, Cloudflare 1006); without a queue we silently dropped
  // every webrtc_offer/webrtc_answer/ice_candidate that hit the
  // wire in that window — producing Wayne's "both see self-view,
  // never see remote" failure mode. We now buffer outbound frames
  // (max 64 to avoid unbounded growth on a permanently-dead socket)
  // and flush them whenever a healthy OPEN socket is observed.
  const pendingOutboundRef = useRef([]);

  const wsSend = useCallback((frame) => {
    const ws = typeof getWs === 'function' ? getWs() : null;
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Drain any frames queued during the last reconnect, then send
      // the current one — preserving original send order.
      try {
        for (const queued of pendingOutboundRef.current) {
          try { ws.send(JSON.stringify(queued)); } catch { /* ignore */ }
        }
        pendingOutboundRef.current = [];
        ws.send(JSON.stringify(frame));
        return true;
      } catch { /* fall through to queue */ }
    }
    if (pendingOutboundRef.current.length < 64) {
      pendingOutboundRef.current.push(frame);
    }

    console.info('[ccuk-call]', 'ws-send-queued', {
      ws_state: ws ? ws.readyState : 'no-ws',
      queue_depth: pendingOutboundRef.current.length,
      frame_type: frame?.type,
    });
    return false;
  }, [getWs]);

  // Whenever the consumer-supplied WS becomes OPEN, drain the queue.
  // We poll once per second (cheap, no per-frame wrappers needed) so
  // that the queue is flushed promptly after a reconnect even if
  // none of the call lifecycle paths happens to call wsSend next.
  useEffect(() => {
    const id = setInterval(() => {
      if (!pendingOutboundRef.current.length) return;
      const ws = typeof getWs === 'function' ? getWs() : null;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const queue = pendingOutboundRef.current;
      pendingOutboundRef.current = [];
      for (const queued of queue) {
        try { ws.send(JSON.stringify(queued)); } catch { /* ignore */ }
      }

      console.info('[ccuk-call]', 'ws-queue-flushed', { count: queue.length });
    }, 1000);
    return () => clearInterval(id);
  }, [getWs]);

  const teardown = useCallback((reason = 'teardown') => {
    // Iter158 — single source of truth for cleanup. The old inline
    // implementation diverged from individual failure paths over
    // time. Now every cancel/decline/end/missed/error path funnels
    // through cleanupCallResources, which clears local stream,
    // peer connection senders/tracks, remote stream, buffers,
    // timers, and the media lock. Idempotent.
    cleanupCallResources(reason);
    setMuted(false);
    setCameraOff(false);
    setCall(null);
    callRef.current = null;
  }, [cleanupCallResources]);

  const ensureIceServers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/calls/ice-servers`, AX);
      if (data?.iceServers?.length) iceServersRef.current = data.iceServers;
      // Surface whether TURN is actually available, so placeCall can
      // decide whether to proceed or bail out before consuming media
      // permissions. Tracked on the ref so we don't trigger a render.
      iceServersRef.turnAvailable = data?.turn === true;
      iceServersRef.warning = data?.warning || null;
    } catch {
      // Keep the public-STUN fallback baked into the hook's initial
      // state. Note that without a backend response we don't know
      // whether TURN would have been available — treat as no-TURN
      // for the cautious path below.
      iceServersRef.turnAvailable = false;
      iceServersRef.warning = 'Could not reach the call server.';
    }
  }, [axios, API, AX]);

  const newPeerConnection = useCallback((initiator = false) => {
    if (typeof RTCPeerConnection === 'undefined') {
      throw new Error('WebRTC not supported in this browser');
    }
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    // Consent messages travel on the encrypted peer connection and are scoped
    // to this call. Older peers without the channel cannot enable recording.
    const attachControls = channel => {
      if (channel.label !== 'ccuk-call-controls-v1') return;
      pc.ccukControls = channel;
      if (pcRef.current === pc && callRef.current) {
        callRef.current = { ...callRef.current, recordingChannel: channel };
        setCall(callRef.current);
      }
    };
    pc.ondatachannel = event => attachControls(event.channel);
    if (initiator && pc.createDataChannel) attachControls(pc.createDataChannel('ccuk-call-controls-v1'));
    // Iter155 — safe diagnostics. Logs only structural facts about
    // the call (call_id, role, track-presence booleans, ICE state)
    // so we can debug WebRTC media issues from a user's browser
    // console. NEVER logs tokens, TURN credentials, JWTs, or SDP.
    const safeLog = (event, extra = {}) => {
      const c = callRef.current;
      if (!c) return;
      const local = localStreamRef.current;
      const remote = remoteStreamRef.current;
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', event, {
        call_id: c.id,
        role: c.direction,
        type: c.type,
        status: c.status,
        hasLocalAudio: !!(local && local.getAudioTracks().length),
        hasLocalVideo: !!(local && local.getVideoTracks().length),
        hasRemoteAudio: !!(remote && remote.getAudioTracks().length),
        hasRemoteVideo: !!(remote && remote.getVideoTracks().length),
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
        signalingState: pc.signalingState,
        ...extra,
      });
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && callRef.current) {
        wsSend({
          type: 'ice_candidate',
          call_id: callRef.current.id,
          conversation_id: callRef.current.conversationId,
          candidate: e.candidate,
        });
        iceSentRef.current += 1;
        safeLog('ice-candidate-sent', { ice_sent_total: iceSentRef.current });
      }
    };
    pc.ontrack = (e) => {
      if (pcRef.current !== pc) return;
      // Some peers send streamless tracks. Retain both audio and video
      // across separate ontrack events instead of dropping those cameras.
      const tracks = new Map();
      remoteStreamRef.current?.getTracks().forEach(track => tracks.set(track.id, track));
      (e.streams || []).forEach(stream => stream.getTracks().forEach(track => tracks.set(track.id, track)));
      if (e.track) tracks.set(e.track.id, e.track);
      const stream = new MediaStream([...tracks.values()]);
      remoteStreamRef.current = stream;
      setRemoteStream(stream);
      safeLog('ontrack', {
        remote_audio_tracks: stream.getAudioTracks().length,
        remote_video_tracks: stream.getVideoTracks().length,
      });
    };
    pc.oniceconnectionstatechange = () => safeLog('ice-state', { iceConnectionState: pc.iceConnectionState });
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return;
      const current = callRef.current;
      if (current) {
        callRef.current = { ...current, connectionState: pc.connectionState };
        setCall(callRef.current);
      }
      safeLog('pc-state', { connectionState: pc.connectionState });
      if (pc.connectionState === 'failed') {
        toast.error('Call connection failed — try again in a moment.');
        endCall();
      }
    };
    return pc;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsSend]);

  const getMedia = useCallback(async (type) => {
    // Defensive — `navigator.mediaDevices` is undefined on insecure
    // contexts (http://) and on a handful of older mobile browsers.
    // Without this guard we'd throw "Cannot read properties of
    // undefined" which surfaces as a generic crash to the user.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast.error('Calls are not supported on this browser. Try the latest Chrome, Safari, or Firefox.');
      const err = new Error('mediaDevices unavailable');
      err.name = 'NotSupportedError';
      throw err;
    }
    const constraints = type === 'video'
      ? { audio: true, video: { facingMode: 'user' } }
      : { audio: true, video: false };
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (err) {
      // Camera failure should fall back to voice — but if mic is also
      // denied we can't make any call at all.
      if (type === 'video' && err?.name === 'NotFoundError') {
        toast.error('No camera found — try voice call instead.');
      } else if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        toast.error("To make calls, allow microphone and camera access in your browser settings, then try again.");
      } else if (err?.name === 'NotFoundError') {
        toast.error('No microphone found on this device.');
      } else if (err?.name === 'NotReadableError' || err?.name === 'TrackStartError') {
        toast.error('Your camera or microphone could not be started. It may still be in use by this site, another tab, or another app. End any active call, close other camera tabs, then try again.');
      } else {
        toast.error('We could not access your microphone — please check your browser settings.');
      }
      throw err;
    }
  }, []);

  // -----------------------------------------------------------------
  // Outgoing — placeCall
  // -----------------------------------------------------------------

  const placeCall = useCallback(async (conversationId, peerId, type, meta = {}) => {
    // Iter158 — synchronous media lock at function entry blocks the
    // double-click/StrictMode race that was producing the misleading
    // "camera/mic in use by another app" toast. Without this, two
    // concurrent placeCall calls would both call getUserMedia; the
    // second one would hit NotReadableError because the first
    // already owns the device.
    if (!acquireMediaLock()) {

      console.info('[ccuk-call]', 'call-click-ignored-in-progress', { direction: 'outgoing' });
      return;
    }
    if (callRef.current) {
      releaseMediaLock();
      toast.error("You're already in a call.");
      return;
    }
    if (!conversationId || !peerId) { releaseMediaLock(); return; }
    // Bail out early if the realtime WebSocket isn't open — we'd
    // otherwise create a call record on the backend but never reach
    // the peer with the offer SDP, leaving the caller stuck on
    // "Calling…" until the 35 s timeout marks the call missed.
    const ws = typeof getWs === 'function' ? getWs() : null;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      releaseMediaLock();
      const now = Date.now();
      if (now - lastNotReadyToastRef.current > 5000) {
        lastNotReadyToastRef.current = now;
        toast.error('Connecting to messages — please wait a few seconds and try again.');
      }
      return;
    }
    await ensureIceServers();
    if (iceServersRef.turnAvailable === false) {
      if (meta?.allowStunOnly) {
        toast.warning(
          'Reader session call: TURN relay not available — the call may not connect if either user is behind a strict firewall.',
          { id: 'audition-stun-only', duration: 5000 },
        );
      } else {
        toast.error('Calling is temporarily unavailable. Please try again later.');
        releaseMediaLock();
        return;
      }
    }
    let stream;
    try {
      stream = await getMedia(type);
    } catch {
      // getMedia already showed a toast. Run cleanup so no stale
      // refs survive into the next attempt.
      cleanupCallResources('getMedia-failed-outgoing');
      return;
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    let started;
    try {
      const { data } = await axios.post(`${API}/api/calls/start`, {
        conversation_id: conversationId,
        call_type: type,
      }, AX);
      started = data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start call');
      cleanupCallResources('start-api-failed');
      return;
    }

    const pc = newPeerConnection(true);
    pcRef.current = pc;
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    const newCall = {
      id: started.call_id,
      recordingChannel: pc.ccukControls,
      conversationId,
      peerId,
      type,
      direction: 'outgoing',
      status: 'ringing',
      peerName: meta.peerName || null,
      peerAvatar: meta.peerAvatar || null,
      startedAt: null,
    };
    callRef.current = newCall;
    setCall(newCall);
    // Iter158 — setup past the failure-prone async work. From here
    // on, further placeCall double-clicks are blocked by the
    // callRef.current guard, so we can release the synchronous
    // media lock to keep the UI responsive (the call buttons
    // un-grey).
    releaseMediaLock();

    // Create + send the SDP offer. The peer will answer once they
    // accept the invite.
    try {
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: type === 'video' });
      await pc.setLocalDescription(offer);
      wsSend({ type: 'webrtc_offer', call_id: started.call_id, conversation_id: conversationId, sdp: offer });
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'offer-sent', {
        call_id: started.call_id,
        call_type: type,
        local_audio_tracks: stream.getAudioTracks().length,
        local_video_tracks: stream.getVideoTracks().length,
      });
    } catch (err) {
      toast.error('Could not start call.');
      try { await axios.post(`${API}/api/calls/${started.call_id}/cancel`, {}, AX); } catch { /* ignore */ }
      teardown();
      return;
    }

    // Ring timeout — if the callee doesn't accept in 35s, mark missed.
    ringTimerRef.current = setTimeout(async () => {
      if (callRef.current?.status === 'ringing') {
        try { await axios.post(`${API}/api/calls/${started.call_id}/missed`, {}, AX); } catch { /* ignore */ }
        toast(`No answer from ${meta.peerName || 'them'}.`);
        teardown();
      }
    }, RING_TIMEOUT_MS);
  }, [axios, API, AX, ensureIceServers, getMedia, newPeerConnection, teardown, wsSend, getWs]);

  // -----------------------------------------------------------------
  // Incoming — invite handler triggers UI; user calls acceptCall/decline
  // -----------------------------------------------------------------

  const handleIncomingInvite = useCallback((frame) => {
    if (callRef.current?.id === frame.call_id) return; // Replayed invite is not a second call.
    if (callRef.current) {
      // Already in a call — auto-decline so the caller hears a clean
      // "declined" rather than ringing forever.
      axios.post(`${API}/api/calls/${frame.call_id}/decline`, {}, AX).catch(() => {});
      return;
    }
    const incoming = {
      id: frame.call_id,
      conversationId: frame.conversation_id,
      peerId: frame.caller_id,
      type: frame.call_type || 'voice',
      direction: 'incoming',
      status: 'ringing',
      peerName: frame.caller_name || 'Incoming call',
      peerAvatar: null, // resolved lazily by the overlay via /api/connections/card
      startedAt: null,
    };
    callRef.current = incoming;
    setCall(incoming);
  }, [axios, API, AX]);

  const acceptCall = useCallback(async (overrideType) => {
    const c = callRef.current;
    if (!c || c.direction !== 'incoming' || c.status !== 'ringing') return;
    // Iter158 — same synchronous lock guard as placeCall. Blocks
    // double-taps on Accept that would otherwise race two
    // getUserMedia calls and produce the "camera in use" false
    // toast on the second attempt.
    if (!acquireMediaLock()) {

      console.info('[ccuk-call]', 'call-click-ignored-in-progress', { direction: 'incoming' });
      return;
    }
    const effectiveType = overrideType || c.type;
    let stream;
    try {
      await ensureIceServers();
      stream = await getMedia(effectiveType);
    } catch {
      // Device/permission failure is not a deliberate decline. Keep the
      // invitation available for voice fallback or retry until caller expiry.
      releaseMediaLock();
      return;
    }
    if (callRef.current?.id !== c.id || callRef.current?.status !== 'ringing') {
      stream.getTracks().forEach(t => t.stop());
      if (!callRef.current || callRef.current.id === c.id) releaseMediaLock();
      return;
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    const pc = newPeerConnection();
    pcRef.current = pc;
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    try {
      await axios.post(`${API}/api/calls/${c.id}/accept`, {}, AX);
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'accept-sent', {
        call_id: c.id,
        call_type: effectiveType,
        local_audio_tracks: stream.getAudioTracks().length,
        local_video_tracks: stream.getVideoTracks().length,
        pending_offer_buffered: !!pendingOfferRef.current,
      });
    } catch (err) {
      toast.error('Could not accept call.');
      teardown();
      return;
    }
    callRef.current = { ...c, recordingChannel: pc.ccukControls, status: 'accepted', connectionState: pc.connectionState || 'connecting', startedAt: new Date().toISOString(), type: effectiveType };
    setCall(callRef.current);
    // Iter158 — past the async-failure-prone setup. Release the
    // sync lock so the overlay's End Call button is responsive.
    // The teardown path is reached via endCall / declineCall /
    // call_ended signal — all of which re-release defensively.
    releaseMediaLock();

    // Iter156 — flush the buffered SDP offer if it arrived BEFORE
    // this Accept tap (the typical race: the offer hits the WS within
    // 50ms of the invite, but the user takes 2-10s to tap). Without
    // this flush the callee would never produce an answer SDP and
    // the call would silently fail.
    if (pendingOfferRef.current) {
      const sdp = pendingOfferRef.current;
      pendingOfferRef.current = null;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        for (const cand of pendingIceRef.current) {
          try { await pc.addIceCandidate(new RTCIceCandidate(cand)); } catch { /* ignore */ }
        }
        pendingIceRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        wsSend({ type: 'webrtc_answer', call_id: c.id, conversation_id: c.conversationId, sdp: answer });
      } catch (err) {
        toast.error('Call negotiation failed.');
        teardown();
      }
    }
  }, [axios, API, AX, ensureIceServers, getMedia, newPeerConnection, teardown, wsSend]);

  const declineCall = useCallback(async () => {
    const c = callRef.current;
    if (!c || c.direction !== 'incoming') return;
    try { await axios.post(`${API}/api/calls/${c.id}/decline`, {}, AX); } catch { /* ignore */ }
    teardown();
  }, [axios, API, AX, teardown]);

  // eslint-disable-next-line no-use-before-define
  const endCall = useCallback(async () => {
    const c = callRef.current;
    if (!c) return;
    try {
      if (c.direction === 'outgoing' && c.status === 'ringing') {
        await axios.post(`${API}/api/calls/${c.id}/cancel`, {}, AX);
      } else {
        await axios.post(`${API}/api/calls/${c.id}/end`, {}, AX);
      }
    } catch { /* ignore */ }
    teardown();
  }, [axios, API, AX, teardown]);

  // -----------------------------------------------------------------
  // Signalling handler — called by the parent WS onmessage hook
  // -----------------------------------------------------------------

  const handleSignal = useCallback(async (frame) => {
    const type = frame?.type;
    if (!type) return;
    const c = callRef.current;

    if (type === 'call_invite') {
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'call-invite-received', { call_id: frame.call_id, call_type: frame.call_type });
      return handleIncomingInvite(frame);
    }

    if (!c || (frame.call_id && frame.call_id !== c.id)) return;

    if (type === 'call_accepted') {
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'call-accepted-received', { call_id: c.id });
      if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; }
      callRef.current = { ...c, recordingChannel: pcRef.current?.ccukControls || c.recordingChannel, status: 'accepted', connectionState: pcRef.current?.connectionState || 'connecting', startedAt: new Date().toISOString() };
      setCall(callRef.current);
      return;
    }
    if (type === 'call_declined' || type === 'call_cancelled' || type === 'call_ended' || type === 'call_missed') {
      // Always notify the user of why the call ended.
      if (type === 'call_declined') {
        toast(`${c.peerName || 'They'} declined the call`);
      }
      else if (type === 'call_cancelled') toast(`${c.peerName || 'They'} cancelled the call`);
      else if (type === 'call_missed') toast(`Missed call from ${c.peerName || 'them'}`);
      teardown();
      if (type === 'call_declined' && c.direction === 'outgoing') {
        outcomeToneRef.current = startCallTone('busy');
      }
      return;
    }

    if (type === 'webrtc_offer') {
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'offer-received', { call_id: c.id, pc_ready: !!pcRef.current });
      // Iter156 — Callee side. The offer arrives within milliseconds
      // of the call_invite, but the user takes seconds to tap Accept,
      // during which `pcRef.current` is null. Previously we dropped
      // the offer on the floor in that window — the callee then had
      // no SDP to answer with, the caller never got a remote
      // description applied, and both sides saw their own video but
      // no remote stream. Buffer the offer so acceptCall can apply
      // it the moment the PC is created.
      if (!pcRef.current) {
        pendingOfferRef.current = frame.sdp;
        return;
      }
      try {
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(frame.sdp));
        // Flush any ICE candidates we received before the remote
        // description was applied.
        for (const cand of pendingIceRef.current) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(cand)); } catch { /* ignore */ }
        }
        pendingIceRef.current = [];
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);
        wsSend({ type: 'webrtc_answer', call_id: c.id, conversation_id: c.conversationId, sdp: answer });
        // eslint-disable-next-line no-console
        console.info('[ccuk-call]', 'answer-sent', { call_id: c.id });
      } catch (err) {
        toast.error('Call negotiation failed.');
        endCall();
      }
      return;
    }
    if (type === 'webrtc_answer') {
      // eslint-disable-next-line no-console
      console.info('[ccuk-call]', 'answer-received', { call_id: c.id });
      // Caller side — apply the SDP answer.
      if (!pcRef.current) return;
      try {
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(frame.sdp));
        for (const cand of pendingIceRef.current) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(cand)); } catch { /* ignore */ }
        }
        pendingIceRef.current = [];
      } catch (err) {
        toast.error('Call negotiation failed.');
        endCall();
      }
      return;
    }
    if (type === 'ice_candidate') {
      iceRcvdRef.current += 1;
      if (!pcRef.current || !pcRef.current.remoteDescription) {
        pendingIceRef.current.push(frame.candidate);
        return;
      }
      try { await pcRef.current.addIceCandidate(new RTCIceCandidate(frame.candidate)); } catch { /* ignore */ }
      // Log every 5 ICE candidates to avoid console spam, plus the first one.
      if (iceRcvdRef.current === 1 || iceRcvdRef.current % 5 === 0) {
        // eslint-disable-next-line no-console
        console.info('[ccuk-call]', 'ice-candidate-received', {
          call_id: c.id, ice_rcvd_total: iceRcvdRef.current,
        });
      }
    }
  }, [handleIncomingInvite, teardown, wsSend, endCall]);

  // -----------------------------------------------------------------
  // Mute / camera toggles
  // -----------------------------------------------------------------

  const toggleMute = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    const next = !muted;
    s.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  const toggleCamera = useCallback(() => {
    const s = localStreamRef.current;
    if (!s) return;
    const next = !cameraOff;
    s.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setCameraOff(next);
  }, [cameraOff]);

  // Clean up on unmount.
  useEffect(() => () => { outcomeToneRef.current?.(); teardown(); }, [teardown]);

  return {
    call,
    muted, cameraOff,
    localStream, remoteStream,
    placeCall, acceptCall, declineCall, endCall,
    toggleMute, toggleCamera,
    handleSignal,
    // Iter158 — true while a getUserMedia + /calls/start (or accept)
    // round-trip is in flight. Consumers should disable the Video
    // Call / Voice Call / Accept buttons while this is true to
    // prevent the double-click race that produced the false
    // "camera in use" toast.
    mediaInProgress,
  };
}

